/**
 * Dual-mode network interceptor.
 *
 * Mode 1 (BiDi): Not yet widely supported for Appium; reserved for future.
 * Mode 2 (Performance logs): Parses Chrome DevTools Protocol network events
 *         from `getLogs('performance')` or `getLogs('safariNetwork')`.
 * Mode 3 (Proxy fallback): When no CDP log type is available and a deviceSerial
 *         is provided, starts a local mockttp proxy to capture traffic from the
 *         emulator (requires ADB access and mockttp package).
 */

import type { AppiumNetworkEntry, Platform } from './types.js';
import { JsonlFileWriter } from './jsonl-stream.js';
import { ProxyNetworkInterceptor } from './proxy-network-interceptor.js';
import { networkEntryToRequest } from './test-data-extractor.js';
import type { CapturedNetworkRequest } from '@testrelic/core';

export interface NetworkInterceptorOptions {
  readonly enabled: boolean;
  readonly platform: Platform;
  readonly pollInterval: number;
  /** ADB device serial — enables the mockttp proxy fallback on emulators */
  readonly deviceSerial?: string;
}

type LogEntry = { level: string; timestamp: number; message: string };
type DriverLike = {
  getLogs(type: string): Promise<LogEntry[]>;
  getLogTypes(): Promise<string[]>;
};

interface ChromeNetworkMessage {
  message: {
    method: string;
    params: Record<string, unknown>;
  };
}

const pendingRequests = new Map<string, Partial<AppiumNetworkEntry>>();

function parsePerformanceLog(rawMessage: string): AppiumNetworkEntry | null {
  let parsed: ChromeNetworkMessage;
  try {
    parsed = JSON.parse(rawMessage) as ChromeNetworkMessage;
  } catch {
    return null;
  }

  const method = parsed.message?.method;
  const params = parsed.message?.params;
  if (!method || !params) return null;

  if (method === 'Network.requestWillBeSent') {
    const request = params.request as Record<string, unknown> | undefined;
    if (!request) return null;
    const requestId = String(params.requestId ?? '');
    const entry: Partial<AppiumNetworkEntry> = {
      url: String(request.url ?? ''),
      method: String(request.method ?? 'GET'),
      timestamp: new Date().toISOString(),
      source: 'performance',
    };
    pendingRequests.set(requestId, entry);
    return null;
  }

  if (method === 'Network.responseReceived') {
    const response = params.response as Record<string, unknown> | undefined;
    if (!response) return null;
    const requestId = String(params.requestId ?? '');
    const pending = pendingRequests.get(requestId);
    if (!pending) return null;
    pendingRequests.delete(requestId);

    return {
      url: pending.url ?? String(response.url ?? ''),
      method: pending.method ?? 'GET',
      status: Number(response.status ?? 0),
      timestamp: pending.timestamp ?? new Date().toISOString(),
      source: 'performance',
      mimeType: typeof response.mimeType === 'string' ? response.mimeType : undefined,
    };
  }

  return null;
}

export class NetworkInterceptor {
  private readonly writer: JsonlFileWriter;
  private readonly options: NetworkInterceptorOptions;
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private driver: DriverLike | null = null;
  private logType: string | null = null;
  private disposed = false;
  private entries: AppiumNetworkEntry[] = [];

  /** Proxy interceptor used when CDP log types are unavailable on emulators. */
  private proxyInterceptor: ProxyNetworkInterceptor | null = null;
  private proxyActive = false;
  /** Offset into proxyInterceptor.entries at the start of each test. */
  private proxyTestStartOffset = 0;

  constructor(options: NetworkInterceptorOptions) {
    this.options = options;
    this.writer = new JsonlFileWriter('network-logs');
  }

  async start(driver: DriverLike): Promise<void> {
    if (!this.options.enabled || this.disposed) return;
    this.driver = driver;

    let available: string[] = [];
    try {
      available = await driver.getLogTypes();
    } catch {
      available = [];
    }

    if (available.includes('performance')) {
      this.logType = 'performance';
    } else if (this.options.platform === 'ios' && available.includes('safariNetwork')) {
      this.logType = 'safariNetwork';
    }

    if (this.logType) {
      this.intervalHandle = setInterval(() => {
        void this.poll();
      }, this.options.pollInterval);
      return;
    }

    // No CDP log type available — try the mockttp proxy fallback on Android emulators
    const serial = this.options.deviceSerial;
    if (serial && this.options.platform === 'android') {
      this.proxyInterceptor = new ProxyNetworkInterceptor(serial);
      this.proxyActive = await this.proxyInterceptor.start();
    }
  }

  /**
   * Snapshot the proxy interceptor's current entry count so that
   * getEntriesSince() returns only this test's network entries.
   * Should be called at beforeTest time (alongside the main offset snapshot).
   */
  snapshotProxyOffset(): void {
    this.proxyTestStartOffset = this.proxyInterceptor?.snapshotOffset() ?? 0;
  }

  private async poll(): Promise<void> {
    if (!this.driver || this.disposed || !this.logType) return;

    try {
      const entries = await this.driver.getLogs(this.logType);
      for (const raw of entries) {
        if (this.logType === 'performance') {
          const parsed = parsePerformanceLog(raw.message);
          if (parsed) {
            this.entries.push(parsed);
            this.writer.append(parsed);
          }
        } else {
          const entry: AppiumNetworkEntry = {
            url: raw.message,
            method: 'GET',
            timestamp: new Date(raw.timestamp).toISOString(),
            source: 'safariNetwork',
          };
          this.entries.push(entry);
          this.writer.append(entry);
        }
      }
    } catch {
      // Silently skip
    }
  }

  getEntries(): AppiumNetworkEntry[] {
    return [...this.entries];
  }

  /**
   * Returns only entries appended after the given offset index (for per-test slicing).
   * When the proxy fallback is active, merges CDP entries with proxy-captured entries.
   */
  getEntriesSince(offset: number): AppiumNetworkEntry[] {
    const cdpEntries = this.entries.slice(offset);

    if (!this.proxyActive || !this.proxyInterceptor) {
      return cdpEntries;
    }

    // Convert proxy CapturedNetworkRequest entries to AppiumNetworkEntry shape
    const proxyCaptured: CapturedNetworkRequest[] =
      this.proxyInterceptor.getEntriesSince(this.proxyTestStartOffset);

    const proxyEntries: AppiumNetworkEntry[] = proxyCaptured.map((req) => ({
      url: req.url,
      method: req.method,
      status: req.statusCode,
      timestamp: req.startedAt,
      source: 'performance' as const,
      mimeType: req.contentType ?? undefined,
      duration: req.responseTimeMs,
      requestHeaders: req.requestHeaders ?? undefined,
      responseHeaders: req.responseHeaders ?? undefined,
      requestBody: req.requestBody ?? undefined,
      responseBody: req.responseBody ?? undefined,
      size: req.responseSize,
    }));

    return [...cdpEntries, ...proxyEntries];
  }

  /**
   * Returns network entries as CapturedNetworkRequest objects.
   * Used by the cloud reporter when uploading structured network logs.
   */
  getCapturedRequestsSince(offset: number): CapturedNetworkRequest[] {
    if (this.proxyActive && this.proxyInterceptor) {
      return this.proxyInterceptor.getEntriesSince(this.proxyTestStartOffset);
    }
    return this.entries.slice(offset).map(networkEntryToRequest);
  }

  getPath(): string {
    return this.writer.getPath();
  }

  getCount(): number {
    return this.writer.getCount();
  }

  stop(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.writer.close();
    if (this.proxyInterceptor) {
      void this.proxyInterceptor.stop();
      this.proxyInterceptor = null;
    }
  }

  cleanup(): void {
    this.dispose();
    this.writer.cleanup();
  }
}

export { parsePerformanceLog };
